import Request from '../../utilities/Request';
import { fetchGroceryLists } from './calendar';
import { foodChangesReceived, isFoodActive, refreshFood } from './food';
import { loadMealIdeas } from './mealIdeas';
import { itemDetailsChanges } from '../food/catalogChanges';

/**
 * Grocery items: the JS side of the `grocery_item` API domain
 * (`models/components/api/grocery_item_domain.php`). The item editor keeps the details it shows to
 * itself, so these hand back the answer; the budget's catalog of items is the food store's
 * (selectGroceryItemCatalog), and every write's answer is folded into it, so a change shows on every
 * screen without a reload.
 *
 * Everything but the lookup answers with the item's details:
 * { item: { id, name, image_url, product_url, brand, tags }, kind_of: { id, name }|null, kinds: [{ id, name }],
 *   budget_tags, meal_ideas: [{ id, name, quantity, kind }], last_bought, bought: ['Y-m-d'], bought_count,
 *   budget_id }
 */

/**
 * Sends a write, `send()`, and folds its answer into the food store's catalog when it echoes the
 * budget the store holds. `askedId` is the item the write was about, which a rename can merge away.
 * Resolves with the answer.
 */
const intoCatalog = (send, askedId = null) => (dispatch, getState) => send().then((details) => {
	if(details && isFoodActive(getState)) {
		dispatch(foodChangesReceived(details.budget_id, itemDetailsChanges(getState().food.confirmed, details, askedId)));
	}
	return details;
});

/** @returns {Promise<Object>} the item's details */
export const fetchGroceryItem = (groceryItemId) => Request.apiv1Get(`grocery_item/show/${groceryItemId}`);

/** @returns {Promise<{id: number, name: string}|null>} the item `name` already means, if any */
export const lookUpGroceryItem = (name) => Request.apiv1Get('grocery_item/lookup', { name })
	.then((response) => response.item || null);

/**
 * A rename changes the grocery lists and the meal ideas, so both are reloaded once it lands.
 * Resolves with the details of the item it leaves the row as.
 */
const renamed = (dispatch, send, askedId = null) => dispatch(intoCatalog(send, askedId)).then((details) => {
	dispatch(fetchGroceryLists());
	dispatch(loadMealIdeas());
	return details;
});

/** Renames the item everywhere, merging it into an item that already has the name. */
export const renameGroceryItem = (groceryItemId, name) => (dispatch) =>
	renamed(dispatch, () => Request.apiv1Post('grocery_item/rename', { grocery_item_id: groceryItemId, name }), groceryItemId);

/** Renames one row of the active list on its own, with the meal idea ingredients it is there for. */
export const splitGroceryListItem = (groceryListItemId, name) => (dispatch) =>
	renamed(dispatch, () => Request.apiv1Post('grocery_item/split', { grocery_list_added_item_id: groceryListItemId, name }));

/**
 * Sets the item's image_url, product_url and brand, whichever `fields` has; '' clears one. Only the
 * item changes, so nothing is reloaded. Resolves with the item's details.
 */
export const updateGroceryItem = (groceryItemId, fields) =>
	intoCatalog(() => Request.apiv1Post('grocery_item/update', { grocery_item_id: groceryItemId, ...fields }));

/**
 * Replaces the item's tags. The lists show them on their rows, so they are reloaded once it lands.
 * Resolves with the item's details.
 */
export const setGroceryItemTags = (groceryItemId, tags) => (dispatch) =>
	dispatch(intoCatalog(() => Request.apiv1Post('grocery_item/set_tags', { grocery_item_id: groceryItemId, tags }))).then((details) => {
		dispatch(fetchGroceryLists());
		return details;
	});

/**
 * Fetches the item's product link and takes its picture, and its brand when the item has none.
 * Resolves with the item's details and `fetch_error`: null when the picture was found, otherwise
 * why not, for the person to read.
 */
export const fetchProductPicture = (groceryItemId) =>
	intoCatalog(() => Request.apiv1Post('grocery_item/fetch_product', { grocery_item_id: groceryItemId }));

/**
 * A change of kinds: the meal ideas name their ingredients' generic and kinds, so the store is
 * refreshed once it lands.
 */
const kindsChanged = (send) => (dispatch) => dispatch(intoCatalog(send)).then((details) => {
	dispatch(refreshFood());
	return details;
});

/**
 * Makes the item a kind of the one named `name`, made if the budget has none; '' makes it a kind of
 * nothing. Resolves with the item's details.
 */
export const setGroceryItemKindOf = (groceryItemId, name) =>
	kindsChanged(() => Request.apiv1Post('grocery_item/set_kind_of', { grocery_item_id: groceryItemId, kind_of: name }));

/** Makes the item named `name`, made if the budget has none, a kind of this one. Resolves with its details. */
export const addGroceryItemKind = (groceryItemId, name) =>
	kindsChanged(() => Request.apiv1Post('grocery_item/add_kind', { grocery_item_id: groceryItemId, name }));
